import React from 'react';
import { Building2, Calendar, Banknote, Percent } from 'lucide-react';

const AssetParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  assetTypes,
  depreciationGroups 
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {/* Название основного средства */}
      <div className="col-span-full">
        <label htmlFor="assetName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Building2 className="w-4 h-4 mr-2 text-indigo-500" />
          Название основного средства
        </label>
        <input
          type="text"
          id="assetName"
          name="assetName"
          value={formData.assetName}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.assetName ? 'input-error' : ''}`}
          placeholder="Введите название актива"
        />
        {errors.assetName && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.assetName}
          </p>
        )}
      </div>

      {/* Первоначальная стоимость */}
      <div>
        <label htmlFor="assetCost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Banknote className="w-4 h-4 mr-2 text-indigo-500" />
          Первоначальная стоимость
        </label>
        <input
          type="text"
          id="assetCost"
          name="assetCost"
          value={formData.assetCost}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.assetCost ? 'input-error' : ''}`}
          placeholder="Введите стоимость"
        />
        {errors.assetCost && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.assetCost}
          </p>
        )}
      </div>

      {/* Ликвидационная стоимость */}
      <div>
        <label htmlFor="salvageValue" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Percent className="w-4 h-4 mr-2 text-indigo-500" />
          Ликвидационная стоимость
        </label>
        <input
          type="text"
          id="salvageValue"
          name="salvageValue"
          value={formData.salvageValue}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.salvageValue ? 'input-error' : ''}`}
          placeholder="Введите ликвидационную стоимость"
        />
        {errors.salvageValue && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.salvageValue}
          </p>
        )}
      </div>

      {/* Срок полезного использования */}
      <div>
        <label htmlFor="usefulLife" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-indigo-500" />
          Срок полезного использования (лет)
        </label>
        <input
          type="text"
          id="usefulLife"
          name="usefulLife"
          value={formData.usefulLife}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.usefulLife ? 'input-error' : ''}`}
          placeholder="Введите срок"
        />
        {errors.usefulLife && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.usefulLife}
          </p>
        )}
      </div>

      {/* Дата начала эксплуатации */}
      <div>
        <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Дата начала эксплуатации
        </label>
        <input
          type="date"
          id="startDate"
          name="startDate"
          value={formData.startDate}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.startDate ? 'input-error' : ''}`}
        />
        {errors.startDate && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.startDate}
          </p>
        )}
      </div>

      {/* Тип основного средства */}
      <div>
        <label htmlFor="assetType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Тип основного средства
        </label>
        <select
          id="assetType"
          name="assetType"
          value={formData.assetType}
          onChange={handleInputChange}
          className="form-select w-full"
        >
          {assetTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>
      </div>

      {/* Амортизационная группа */}
      <div>
        <label htmlFor="assetGroup" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Амортизационная группа
        </label>
        <select
          id="assetGroup"
          name="assetGroup"
          value={formData.assetGroup}
          onChange={handleInputChange}
          className="form-select w-full"
        >
          {depreciationGroups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Группы по Классификации основных средств (Постановление Правительства РФ № 1)
        </p>
      </div>

      {/* Информационный блок по амортизационной группе */}
      <div className="col-span-full mt-2 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800">
        <div className="flex items-start">
          <Building2 className="w-4 h-4 text-blue-500 mt-1 mr-2" />
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300">
              Подсказка по выбору группы амортизации
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
              Выбранная группа ({formData.assetGroup}) определяет срок полезного использования 
              {depreciationGroups.find(g => g.id === formData.assetGroup)?.minYears && 
                ` от ${depreciationGroups.find(g => g.id === formData.assetGroup)?.minYears} до 
                ${depreciationGroups.find(g => g.id === formData.assetGroup)?.maxYears} лет`}. 
              Правильное определение группы влияет на норму амортизации для налогового учета.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetParametersSection;